/**
 * Utility functions for detecting and handling 22-inch portrait displays
 */

/**
 * Detects if the current device is a 22-inch portrait display (1080x1920)
 */
export const is22InchPortrait = (): boolean => {
  return (
    // Check screen dimensions
    (window.screen.width === 1080 && window.screen.height === 1920) ||
    // Fallback to inner dimensions
    (window.innerWidth === 1080 && window.innerHeight === 1920)
  );
};

/**
 * Applies special styles to handle the 22-inch portrait mode
 * @param element The element to apply styles to
 */
export const apply22InchPortraitStyles = (element: HTMLElement): void => {
  if (!is22InchPortrait()) return;
  
  // Apply portrait-specific styles
  element.style.width = '1080px';
  element.style.maxWidth = '1080px';
  element.style.left = '0';
  element.style.right = '0';
  element.style.overflow = 'hidden';
  
  // Apply to body as well
  document.body.style.width = '1080px';
  document.body.style.maxWidth = '1080px';
  document.body.style.overflow = 'hidden';
};

/**
 * Add to component useEffect to force correct viewport on 22-inch displays
 */
export const setupPortraitViewport = (): (() => void) => {
  if (!is22InchPortrait()) return () => {};
  
  // Force the viewport
  const viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) {
    viewport.setAttribute(
      'content', 
      'width=1080, height=1920, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
    );
  }
  
  // Add animation for portrait spinner if it doesn't exist
  if (!document.getElementById('portrait-spinner-style')) {
    const styleEl = document.createElement('style');
    styleEl.id = 'portrait-spinner-style';
    styleEl.innerHTML = `
      @keyframes portraitSpin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .portrait-spinner {
        display: inline-block;
        width: 50px;
        height: 50px;
        border: 4px solid rgba(2, 81, 224, 0.2);
        border-left-color: #0251E0;
        border-radius: 50%;
        animation: portraitSpin 1s linear infinite;
      }
    `;
    document.head.appendChild(styleEl);
  }
  
  // Create a direct DOM observer to fix any loading screens as soon as they appear
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.addedNodes.length) {
        const loadingElements = document.querySelectorAll('.loading-container');
        loadingElements.forEach((el: Element) => {
          if (el instanceof HTMLElement) {
            el.style.width = '1080px';
            el.style.maxWidth = '1080px';
            el.style.left = '0';
          }
        });
      }
    });
  });
  
  // Start observing
  observer.observe(document.body, { childList: true, subtree: true });
  
  // Also set an interval as a backup to continuously check for loading screens
  const interval = setInterval(() => {
    const loadingElements = document.querySelectorAll('.loading-container');
    loadingElements.forEach((el: Element) => {
      if (el instanceof HTMLElement) {
        el.style.width = '1080px';
        el.style.maxWidth = '1080px';
        el.style.left = '0';
      }
    });
  }, 100);
  
  // Return cleanup function
  return () => {
    clearInterval(interval);
    observer.disconnect();
    const styleEl = document.getElementById('portrait-spinner-style');
    if (styleEl) {
      styleEl.remove();
    }
  };
};

const portraitModeDetector = {
  is22InchPortrait,
  apply22InchPortraitStyles,
  setupPortraitViewport
};

export default portraitModeDetector; 